import { useEntitlements, useTenant } from "@how/app-kit";
import {
  IconCalendarEvent,
  IconCalendarUser,
  IconCircleCheck,
  IconMicrophone2,
  IconUserCircle,
  IconUsersGroup,
} from "@tabler/icons-react";
import type { ModuleKey } from "@how/types";
import { useMemo } from "react";
import {
  HomeDashboard,
  type HomeDesign,
  greetingFor,
  type DashboardAction,
  type DashboardBreakdown,
  type DashboardData,
  type DashboardFeature,
  type DashboardJourney,
  type DashboardTone,
} from "@how/ui/dashboard";
import { formatWhen, useUpcomingEvents } from "../events/event-queries";
import { usePortalSermons } from "../sermons/sermon-queries";
import { useMySchedule } from "../my-schedule/church-schedule-queries";
import { useMyProfile } from "../profile/profile-queries";
import { useOwnProfile, useTenantRecord } from "../tenant/queries";
import { DevotionalCard } from "./DevotionalCard";
import { STANDING_WORDS, useMyPathway } from "./pathway-queries";
import { useWaitingItems, type WaitingKind } from "./WaitingCard";
import { DISPLAY_LOCALE } from "@how/ui";

/** How each kind of waiting item looks and what its button says on its card. */
const WAITING: Record<WaitingKind, { readonly tone: DashboardTone; readonly action: string }> = {
  group_requests: { tone: "amber", action: "Review" },
  serving: { tone: "iris", action: "Answer" },
  my_request: { tone: "sky", action: "See groups" },
  event: { tone: "mint", action: "See event" },
  care: { tone: "sky", action: "See requests" },
};

function formatDay(isoDate: string): string {
  // A sermon's date is a calendar day, not an instant: read it at noon so no time zone moves it a day.
  return new Date(`${isoDate}T12:00:00`).toLocaleDateString(DISPLAY_LOCALE, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/**
 * The member's home page in the Horizon design. It leads with what is waiting for the member, under the greeting, then
 * their way to membership, then the latest sermon and what is coming up. The church's own settings (time zone, host)
 * are staff matters and are left out.
 */
export function HomePage({ design = "horizon" }: { readonly design?: HomeDesign } = {}) {
  const tenant = useTenant();
  const record = useTenantRecord();
  const profile = useOwnProfile();
  // The person the login is linked to, by the name they go by; a login not linked to anyone keeps its own name.
  const person = useMyProfile().data?.person;
  const greetingName =
    person === undefined
      ? profile.data?.display_name
      : `${person.preferredName?.trim() || person.firstName} ${person.lastName}`;
  const entitlements = useEntitlements();
  const waiting = useWaitingItems();
  const events = useUpcomingEvents();
  const schedule = useMySchedule();
  const sermons = usePortalSermons();
  const pathway = useMyPathway();
  const now = useMemo(() => new Date(), []);

  const has = (module: ModuleKey) => entitlements.data?.has(module) === true;
  const occurrences = (events.data ?? []).filter((o) => !o.isCancelled);
  const going = occurrences.filter((o) =>
    o.mine.some((m) => m.isMe && (m.status === "registered" || m.status === "waitlisted")),
  );
  const serving =
    schedule.data === undefined || !schedule.data.linked
      ? null
      : schedule.data.assignments.filter(
          (a) =>
            !a.isCancelled &&
            a.response !== "declined" &&
            new Date(a.startsAt).getTime() >= now.getTime(),
        ).length;
  const name = record.data?.name ?? (tenant.status === "resolved" ? tenant.tenant.slug : "");

  // The newest sermon already preached; the list comes newest first.
  const today = now.toISOString().slice(0, 10);
  const latest = (sermons.data ?? []).find((s) => s.preachedOn <= today);
  let feature: DashboardFeature | null = null;
  if (sermons.isPending && has("sermons_media")) {
    feature = {
      title: "Latest sermon",
      heading: "",
      meta: [],
      icon: IconMicrophone2,
      action: { label: "Watch", to: "/sermons" },
      loading: true,
    };
  } else if (latest !== undefined) {
    feature = {
      title: "Latest sermon",
      heading: latest.title,
      meta: [
        ...(latest.series === null ? [] : [latest.series.title]),
        ...(latest.speaker === null ? [] : [latest.speaker.name]),
        formatDay(latest.preachedOn),
      ],
      ...(latest.summary === null ? {} : { body: latest.summary }),
      ...(latest.scripture.length === 0 ? {} : { tag: latest.scripture.join(", ") }),
      icon: IconMicrophone2,
      action: {
        label:
          latest.video !== null ? "Watch" : latest.audio !== null ? "Listen" : "Read the notes",
        to: `/sermons?q=${encodeURIComponent(latest.title)}`,
      },
    };
  }

  // Becoming a member: right after what is waiting. Nothing at all when the church does not have
  // Membership, the login is not linked to a record, or there is neither a standing nor a pathway to show.
  const v = pathway.data;
  let journey: DashboardJourney | null = null;
  if (pathway.isPending && has("membership")) {
    journey = { title: "Becoming a member", steps: [], loading: true };
  } else if (v?.linked === true) {
    const standing = v.standing ?? null;
    const steps = v.steps ?? [];
    const title = v.pathway?.name ?? "Becoming a member";
    const intro = v.pathway?.description ?? null;
    if (standing === "member") {
      journey = {
        title,
        steps: [],
        member: v.since === null || v.since === undefined ? {} : { since: formatDay(v.since) },
      };
    } else if (standing !== null || steps.length > 0) {
      journey = {
        title,
        ...(standing === null ? {} : { standing: `${STANDING_WORDS[standing]}.` }),
        ...(intro === null ? {} : { intro }),
        steps: steps.map((s) => ({
          key: s.id,
          name: s.name,
          ...(s.description === null ? {} : { description: s.description }),
          ...(s.completedOn === null ? {} : { doneOn: formatDay(s.completedOn) }),
        })),
        ...(steps.length === 0
          ? {}
          : { note: "Your church ticks each step off with you as you go." }),
      };
    }
  }

  // The ring shows what kind of dates are coming; the pathway has its own section now.
  let breakdown: DashboardBreakdown | null = null;
  if (!events.isError && occurrences.length > 0) {
    const byCategory = new Map<string, number>();
    for (const o of occurrences) {
      const key = o.event.category ?? "Other";
      byCategory.set(key, (byCategory.get(key) ?? 0) + 1);
    }
    breakdown = {
      title: "What kind of dates",
      caption: "The next two months, by category",
      parts: [...byCategory.entries()]
        .map(([label, value]) => ({ label, value }))
        .sort((a, b) => b.value - a.value),
      totalLabel: "dates",
    };
  }

  const actions: DashboardAction[] = [
    ...(has("events") ? [{ label: "Events", to: "/events", icon: IconCalendarEvent }] : []),
    ...(has("groups") ? [{ label: "Groups", to: "/groups", icon: IconUsersGroup }] : []),
    ...(has("services")
      ? [{ label: "My schedule", to: "/my-schedule", icon: IconCalendarUser }]
      : []),
    ...(has("sermons_media") ? [{ label: "Sermons", to: "/sermons", icon: IconMicrophone2 }] : []),
    { label: "My profile", to: "/profile", icon: IconUserCircle },
  ];

  const data: DashboardData = {
    surface: "Member portal",
    greeting: greetingFor(now, greetingName),
    subtitle: `Welcome home to ${name === "" ? "your church" : name}. What needs you, what is on and where you serve, in one place.`,
    primary: {
      title: "Waiting for you",
      caption: "Answers others need from you, and what is next for you",
      loading: waiting.loading,
      empty: "Nothing waiting. Enjoy your week.",
      items: waiting.items.map((item) => ({
        key: item.key,
        text: item.text,
        to: item.to,
        icon: item.icon,
        ...WAITING[item.kind],
      })),
    },
    journey,
    metrics: [
      {
        key: "coming",
        label: "Coming up",
        value: events.isError ? null : occurrences.length,
        loading: events.isPending,
        hint: "Dates in the next two months",
        icon: IconCalendarEvent,
        tone: "sky",
        ...(has("events") ? { to: "/events" } : {}),
      },
      {
        key: "going",
        label: "I'm going",
        value: events.isError ? null : going.length,
        loading: events.isPending,
        hint: "Dates you signed up for",
        icon: IconCircleCheck,
        tone: "mint",
      },
      {
        key: "serving",
        label: "Serving",
        value: schedule.isError ? null : serving,
        loading: schedule.isPending,
        hint: "Dates on your schedule",
        icon: IconCalendarUser,
        tone: "iris",
        ...(has("services") ? { to: "/my-schedule" } : {}),
      },
      {
        key: "sermons",
        label: "Sermons",
        value: sermons.isError ? null : (sermons.data?.length ?? null),
        loading: sermons.isPending,
        hint: "To watch and listen to",
        icon: IconMicrophone2,
        tone: "amber",
        ...(has("sermons_media") ? { to: "/sermons" } : {}),
      },
    ],
    feature,
    // No chart for members: a count of dates per week said nothing they could act on.
    trend: null,
    breakdown,
    list: {
      title: "Next up",
      caption: "The next dates you can come to",
      loading: events.isPending,
      empty: events.isError ? "Events are not open here yet." : "Nothing on the calendar yet.",
      items: occurrences.slice(0, 5).map((o) => {
        const mine = o.mine.find((m) => m.isMe);
        const where = o.event.location ?? o.event.campus?.name ?? null;
        return {
          id: o.occurrenceId,
          title: o.event.title,
          ...(where === null ? {} : { meta: where }),
          when: formatWhen(o.startsAt, o.event.campus?.timeZone),
          ...(mine === undefined || mine.status === "cancelled"
            ? {}
            : { badge: mine.status === "waitlisted" ? "Waitlisted" : "Going" }),
        };
      }),
      ...(has("events") ? { more: { label: "All events", to: "/events" } } : {}),
    },
    facts: null,
    // Today's devotional (ADR 0077), beside what is coming up; it draws nothing when there is none to read.
    extra: <DevotionalCard />,
    actions,
  };

  return <HomeDashboard data={data} design={design} />;
}
